import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  static targets = ["modal", "title", "description", "submitButton"]
  static values = { url: String }

  connect() {
    this.selectedCount = 0
  }

  open(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    if (selectedIds.length === 0) return

    this.selectedCount = selectedIds.length
    this.updateCopy(selectedIds.length)
    Modal.getOrCreateInstance(this.modalTarget).show()
  }

  async save(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    if (selectedIds.length === 0 || !this.hasUrlValue) return

    this.setSaving(true)

    const formData = new FormData()
    selectedIds.forEach((id) => formData.append("company_user_ids[]", id))

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.removeRows(data.deactivated_ids || [])
        this.clearSelection()
        Modal.getOrCreateInstance(this.modalTarget).hide()

        if (data.skipped_count > 0) {
          const deactivatedCount = data.deactivated_count || 0
          showHubTeamToast(
            `${deactivatedCount} member(s) deactivated. ${data.skipped_count} could not be deactivated.`
          )
        }
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        showHubTeamToast(`Failed to deactivate members: ${message}`, { variant: "error" })
      }
    } catch (error) {
      console.error("Error deactivating company members:", error)
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    } finally {
      this.setSaving(false)
    }
  }

  updateCopy(count) {
    const noun = count === 1 ? "member" : "members"

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Deactivate ${count} ${noun}`
    }

    if (this.hasDescriptionTarget) {
      this.descriptionTarget.textContent =
        `${count} ${noun} will lose access to this team. This action cannot be undone.`
    }

    if (this.hasSubmitButtonTarget && this.submitButtonTarget.dataset.saving !== "true") {
      this.submitButtonTarget.textContent = count === 1 ? "Deactivate 1 member" : `Deactivate ${count} members`
    }
  }

  selectedIds() {
    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')]
      .filter((checkbox) => !checkbox.closest("tr[hidden]"))
      .map((checkbox) => checkbox.value)
  }

  removeRows(deactivatedIds) {
    const ids = new Set(deactivatedIds.map(String))
    if (ids.size === 0) return

    const table = this.element.querySelector("[data-controller~='data-table']")
    let removedCount = 0

    ids.forEach((id) => {
      const row = this.element.querySelector(`tr[data-company-user-id="${id}"]`)
      if (!row) return

      row.remove()
      removedCount += 1
    })

    this.updateMemberCount(-removedCount)

    if (table) {
      const remainingRows = table.querySelectorAll("tbody tr")
      const emptyState = table.querySelector("[data-data-table-target='noResults']")
      if (emptyState) emptyState.hidden = remainingRows.length > 0
    }

    const editPanel = this.element.querySelector("[data-edit-member-panel-target='panel']")
    if (editPanel && !editPanel.hidden) {
      const editPanelController = this.application.getControllerForElementAndIdentifier(this.element, "edit-member-panel")
      editPanelController?.close()
    }
  }

  updateMemberCount(delta) {
    const countElement = this.element.querySelector(".hub-team-count")
    if (!countElement) return

    const currentCount = parseInt(countElement.textContent, 10) || 0
    countElement.textContent = Math.max(0, currentCount + delta)
  }

  clearSelection() {
    const bulkSelectElement = this.element.querySelector('[data-controller~="bulk-select"]')
    if (!bulkSelectElement) return

    const controller = this.application.getControllerForElementAndIdentifier(bulkSelectElement, "bulk-select")
    controller?.clearSelection()
  }

  setSaving(saving) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.dataset.saving = saving ? "true" : "false"
    this.submitButtonTarget.disabled = saving

    if (saving) {
      this.submitButtonTarget.textContent = "Deactivating..."
    } else {
      this.updateCopy(this.selectedCount)
    }
  }
}
